import { useContext, useEffect, useState } from "react";
import { styled, useTheme } from "styled-components";
import {
  getApiKeys,
  createApiKey,
  getGroups,
  getUsers,
} from "modules/agentMySecurity/services/embeddingsService";
import { Toast as showToast } from "core/components/global/toast/toast";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";

import { ApiKey, Group } from "../../admin.type";
import CloseIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Autocomplete,
  Button,
  IconButton,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import {
  AdminTitle,
  AdminGroupsForm,
  ValidatorText,
  AdminUserDetails,
  AdminUserGroupsChip,
  AdminAutocompleteForm,
  AdminUserUpdateContainer,
  AdminModalInnerContainer,
  AdminModal,
  CreateApiKeyModalContainer,
  ApiKeyModalBody,
  AdminFormGroup,
  AdminModalBody,
  AdminModalButtonContainer,
  SimpleTableContainer,
} from "../../admin.styled";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import AdminGroup from "./apiKey";
import { v4 as uuidv4 } from "uuid";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";
import { useAppTheme } from "core/contexts/theme/theme";
import { AuthContext } from "core/contexts/auth/auth";

// Define sort types
type SortColumn = 'description' | 'createdAt' | 'created_by' | null;
type SortDirection = 'asc' | 'desc' | null;

export default function AdminApiKey() {
  const theme = useTheme();
  const [groups, setGroups] = useState<Group[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKey, setApiKey] = useState<string>("");
  const [inputedGroups, setInputedGroups] = useState<Group[]>([]);
  const [description, setDescription] = useState<string>("");
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [modal1Open, setModal1Open] = useState<boolean>(false);
  const [modal2Open, setModal2Open] = useState<boolean>(false);
  const [showApiValidator, setShowApiValidator] = useState<boolean>(false);
  const [copyOfSearchValue, setCopyOfSearchValue] = useState("");
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [filteredGroupsList, setFilteredGroupsList] = useState<Group[]>([]);
  
  // Column sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filteredKeys, setfilteredKeys] = useState<Group[] | null>(null);

  const { amsUser } = useContext(AuthContext);

  const { apptheme } = useAppTheme()

  async function fetchApiKeys() {
    try {
      let keysResposne = await getApiKeys();
      const usersResponse = await getUsers();

      // Create a map for quick lookup
      const userMap = new Map(usersResponse.map(user => [user._id, user.email]));

      // Replace created_by with email if match found
      keysResposne = keysResposne.map(item => ({
        ...item,
        created_by: userMap.get(item.created_by) || item.created_by
      }));
      
      // Reorder them by time so the table is pre-ordered
      const newData = (keysResposne as ApiKey[]).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      setApiKeys(newData);
    } catch (error) {
      setApiKeys([]);
    }
  }

  useEffect(() => {
    async function fetchGroups() {
      try {
        const response = await getGroups();
        setGroups(response);
        setFilteredGroupsList(response);
      } catch (error) {
        setGroups([]);
        setFilteredGroupsList([]);
      }
    }

    fetchApiKeys();
    fetchGroups();
  }, []);
  
  // Filter groups based on search query
  useEffect(() => {
    if (groups.length > 0) {
      if (groupSearchQuery.trim() === "") {
        setFilteredGroupsList(groups);
      } else {
        const filtered = groups.filter(group => 
          group.name.toLowerCase().includes(groupSearchQuery.toLowerCase())
        );
        setFilteredGroupsList(filtered);
      }
    }
  }, [groupSearchQuery, groups]);

  /**
   * Close Modal 1, open Modal 2, when a key has been created
   */
  useEffect(() => {
    if (apiKey !== "") {
      setModal1Open(false);
      setModal2Open(true);
    }
  }, [apiKey]);

  /**
   * Apply sorting whenever sort parameters change
   */
  useEffect(() => {
    if (!sortColumn || !sortDirection) {
      return; // No sorting applied
    }

    // Apply sorting to the data that's currently displayed
    // (either filtered or all data)
    const dataToSort = [...(filteredKeys === null ? apiKeys : filteredKeys)];
    
    const sortedData: any = dataToSort.sort((a, b) => {
      // Default comparison for undefined values
      if (a[sortColumn] === undefined && b[sortColumn] === undefined) return 0;
      if (a[sortColumn] === undefined) return 1;
      if (b[sortColumn] === undefined) return -1;
      
      let comparison = 0;
      
      // Special handling for dates
      if (sortColumn === 'createdAt') {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        comparison = dateA - dateB;
      } 
      // String comparison for other fields
      else {
        const valueA = String(a[sortColumn]).toLowerCase();
        const valueB = String(b[sortColumn]).toLowerCase();
        
        if (valueA < valueB) {
          comparison = -1;
        } else if (valueA > valueB) {
          comparison = 1;
        }
      }
      
      // Reverse for descending order
      return sortDirection === 'desc' ? -comparison : comparison;
    });
    
    // Update the appropriate state based on whether we're sorting filtered or all data
    if (filteredKeys === null) {
      setApiKeys(sortedData);
    } else {
      setfilteredKeys(sortedData);
    }
  }, [sortColumn, sortDirection]);

  /**
   * Copy the key to the clipboard
   */
  const copyText = async () => {
    if (apiKey !== "") {
      const success = await copyToClipboard(apiKey);
      if (!success) {
        showToast(
          "Copy failed, no permission or clipboard support, update your browser permissions.",
          "error"
        );
      }
    }
  };

  /**
   * Remotely update the list of groups (close icon)
   * @param event
   * @param groups
   */
  function handleUpdateNewGroup(event, groups) {
    const { name, value } = event.target;

    if (!name) {
      setInputedGroups(groups);
    } else if (name === "description") {
      if (value.length <= 500) {
        setDescription(value);
    } 
  }
}

  /**
   * Remotely update the list of groups (close icon)
   * @param groupNameToRemove
   */
  const handleRemoveGroup = (groupNameToRemove: string) => {
    // Update the curent inputed state
    setInputedGroups(
      inputedGroups.filter((group) => group.name !== groupNameToRemove)
    );
  };
  
  // Handle group selection in the table
  const handleGroupSelection = (group: Group) => {
    setInputedGroups(prevState => {
      const isSelected = prevState.some(g => g._id === group._id);
      
      if (isSelected) {
        // Remove group if already selected
        return prevState.filter(g => g._id !== group._id);
      } else {
        // Add group if not selected
        return [...prevState, group];
      }
    });
  };
  
  // Check if a group is selected
  const isGroupSelected = (group: Group) => {
    return inputedGroups.some(g => g._id === group._id);
  };
  
  // Handle select all groups
  const handleSelectAllGroups = () => {
    setInputedGroups([...filteredGroupsList]);
  };
  
  // Handle clear all groups
  const handleClearAllGroups = () => {
    setInputedGroups([]);
  };

  /**
   * Submit a new key to the backend
   * If a key successfully created:
   * - show a success message
   * -
   */
  async function handleSubmitApiKey() {
    if (description === "") {
      setShowApiValidator(true);
    }

    if (description !== "") {
      const response = await createApiKey({
        description: description,
        groups: inputedGroups.map((group) => group.name),
      });

      if (response) {
        showToast(response.message, "success");
        setApiKey(response.key);

        // Clear the data for a new key
        setInputedGroups([]);
        setDescription("");
        
        // Refresh the API keys list by fetching the latest data
        fetchApiKeys();
      }
    }
  }

  /**
   * Handle column header click to sort
  */
  const handleSort = (column: SortColumn) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection('asc');
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  const handleFilterData = (newKeys, b, c) => {
    setfilteredKeys(newKeys);
    // Reset sorting when search changes
    setSortColumn(null);
    setSortDirection(null);
  };

  const handleClearFilter = () => {
    setfilteredKeys(null);
  };

  useEffect(() => {
    if(modal1Open === false) {
      setShowApiValidator(false);
    }
  }, [modal1Open])

  useEffect(() => {
    if(modal2Open === false) {
      fetchApiKeys();
    }
  }, [modal2Open])

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    
    return sortDirection === 'asc' ? 
      <ArrowUpwardIcon fontSize="small" className="ms-1" /> : 
      <ArrowDownwardIcon fontSize="small" className="ms-1" />;
  };

  const handleApiKeyDelete = (deletedKeyId) => {

    if (filteredKeys !== null) {
      setfilteredKeys(prevKeys => {
        if (prevKeys === null) return null;
        return prevKeys.filter(key => key._id !== deletedKeyId);
      });
    }
    setApiKeys(prevKeys => prevKeys.filter(key => key._id !== deletedKeyId));
  };

  return (
    <>
      <SimpleTableContainer elevation={1} columns={3}>
        {/* Data Search */}
        <div className="pb-3 d-flex justify-content-between align-items-center">
          <h5>Api Keys</h5>
          <AdvanceSearch
            initialData={apiKeys}
            setFilteredData={handleFilterData}
            clearFilteredData={handleClearFilter}
            setCopyOfSearchValue={setCopyOfSearchValue}
          />
        </div>

        {/* Collapsable Table */}
        <div aria-label="collapsible table">
          {/* Headers */}
          <div
            style={{ borderRadius: "5px 5px 0 0" }}
            className="outer-header-container"
          >
            <div className="row-items row-headers">
              <p 
                className="header-item d-flex align-items-center"
                onClick={() => handleSort('description')}
                style={{ cursor: 'pointer' }}
              >
                Description
                {getSortIcon('description')}
              </p>
              <p 
                className="header-item d-flex align-items-center"
                onClick={() => handleSort('createdAt')}
                style={{ cursor: 'pointer' }}
              >
                Created At
                {getSortIcon('createdAt')}
              </p>
              <p 
                className="header-item d-flex align-items-center"
                onClick={() => handleSort('created_by')}
                style={{ cursor: 'pointer' }}
              >
                Created By
                {getSortIcon('created_by')}
              </p>
            </div>
            <div className="row-buttons">
              <IconButton
                aria-label="send prompt"
                onClick={() => setModal1Open(true)}
                edge="end"
                className="me-1 icon-button"
              >
                <AddIcon />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          <div
            style={{ borderRadius: "0 0 5px 5px", minHeight: "406px" }}
            className="outer-row-container"
          >
            {/* Check if there are any results to show */}
            {(filteredKeys === null ? apiKeys : filteredKeys).length > 0 ? (
              // If there are results, show them as before
              filteredKeys === null ? (
                <>
                  {apiKeys.map((apiKey) => (
                    <AdminGroup
                      key={`row-${apiKey._id}`}
                      apiKey={apiKey}
                      groups={groups}
                      onDelete={handleApiKeyDelete}
                    />
                  ))}
                </>
              ) : (
                <>
                  {filteredKeys.map((apiKey) => (
                    <AdminGroup
                      key={`row-${apiKey._id}`}
                      apiKey={apiKey}
                      groups={groups}
                      onDelete={handleApiKeyDelete}
                    />
                  ))}
                </>
              )
            ) : (
              // If no results, show the NoResultsFound component
              <NoResultsFound
                searchTerm={filteredKeys !== null ? copyOfSearchValue : ""}
              />
            )}
          </div>
        </div>
      </SimpleTableContainer>

      {/* (1) Create Key Modal */}
      <AdminModal
        open={modal1Open}
        onClose={() => setModal1Open(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AdminModalInnerContainer elevation={2}>
          <AdminTitle>
            <div className="d-flex">
              <p className="ps-1">Create an API Key</p>
            </div>
          </AdminTitle>
          <AdminModalBody>
            <AdminGroupsForm>
              <AdminFormGroup>
                <label>Key Description</label>
                <textarea
                  style={{color: theme.colors.white}}
                  onChange={(event) => handleUpdateNewGroup(event, "")}
                  placeholder="Description"
                  className="recessed form-control autoForm"
                  name="description"
                  aria-placeholder="Describe the group"
                  maxLength={500}
                />
                {showApiValidator && (
                  <ValidatorText>
                    <p>A description can't be empty</p>
                  </ValidatorText>
                )}
              </AdminFormGroup>
              <AdminFormGroup>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ marginBottom: 0, marginRight: '8px' }}>Assign Groups to Your Key</label>
                  <span style={{ 
                    backgroundColor: theme.colors.sidebarcolor, 
                    color: theme.colors.white, 
                    borderRadius: '16px', 
                    padding: '2px 8px', 
                    fontSize: theme.font.small, 
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                    height: '20px'
                  }}>
                    {inputedGroups.length}
                  </span>
                </div>
                
                {/* Group selection table */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    {/* Search box */}
                    <TextField
                      placeholder="Search groups..."
                      variant="outlined"
                      size="small"
                      value={groupSearchQuery}
                      onChange={(e) => setGroupSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" className="ms-2">
                            <SearchIcon style={{ color: theme.colors.white }}/>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flexGrow: 1,
                        margin: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.colors.light,
                          borderRadius: '4px',
                          margin: 0,
                          padding: 0,
                          height: '37.5px',
                          '& fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: theme.colors.white,
                          fontSize: theme.font.h4,
                          padding: '5px 12px',
                          height: '24px',
                          border: "none"
                        },
                        '& .MuiInputBase-input:active': {
                          border: "none"
                        },
                      }}
                    />
                    
                    {/* Bulk action buttons */}
                    <div className="d-flex gap-2 ms-2">
                      <Button 
                        variant="contained"
                        onClick={handleSelectAllGroups}
                        sx={{
                          backgroundColor: theme.colors.sidebarcolor,
                          color: theme.colors.white,
                          textTransform: 'none',
                          fontSize: theme.font.h4,
                          padding: '4px 12px',
                          minWidth: '80px',
                          '&:hover': {
                            backgroundColor: theme.name === 'light-theme' 
                              ? theme.colors.loginBackgroundHover 
                              : theme.colors.sidebarLightHover,
                          }
                        }}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="contained"
                        onClick={handleClearAllGroups}
                        sx={{
                          backgroundColor: theme.colors.sidebarcolor,
                          color: theme.colors.white,
                          textTransform: 'none',
                          fontSize: theme.font.h4,
                          padding: '4px 12px',
                          minWidth: '80px',
                          '&:hover': {
                            backgroundColor: theme.name === 'light-theme' 
                              ? theme.colors.loginBackgroundHover 
                              : theme.colors.sidebarLightHover,
                          }
                        }}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Group table */}
                  <TableContainer
                    component={Paper}
                    style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      borderRadius: theme.borders.borderRadius,
                      boxShadow: 'none',
                      backgroundColor: theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            padding="checkbox"
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`
                            }}
                          >
                            <Checkbox
                              indeterminate={
                                inputedGroups.length > 0 &&
                                inputedGroups.length < filteredGroupsList.length
                              }
                              checked={
                                filteredGroupsList.length > 0 &&
                                inputedGroups.length === filteredGroupsList.length
                              }
                              onChange={e => {
                                if (e.target.checked) {
                                  handleSelectAllGroups();
                                } else {
                                  handleClearAllGroups();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`,
                              fontSize: theme.font.h4,
                              fontWeight: 600,
                              color: theme.name === 'light-theme' ? theme.colors.textColor : theme.colors.white,
                              padding: '12px 16px'
                            }}
                          >
                            Group Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredGroupsList.length > 0 ? (
                          filteredGroupsList.map((group) => (
                            <TableRow 
                              key={group._id}
                              selected={isGroupSelected(group)}
                              onClick={() => handleGroupSelection(group)}
                              hover
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: isGroupSelected(group) 
                                  ? theme.colors.historyItemselect 
                                  : theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground,
                                borderBottom: `1px solid ${theme.colors.lightGrey}`
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.colors.historyItemhvr,
                                },
                              }}
                            >
                              <TableCell 
                                padding="checkbox"
                                style={{
                                  padding: '10px 0 10px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                <Checkbox 
                                  checked={isGroupSelected(group)}
                                  style={{
                                    color: theme.colors.sidebarcolor
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                style={{
                                  fontSize: theme.font.h4,
                                  color: theme.colors.textColor,
                                  padding: '12px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                {group.name}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell 
                              colSpan={2} 
                              align="center"
                              style={{
                                padding: '16px',
                                color: theme.colors.textDisabledColor,
                                fontSize: theme.font.h4,
                                borderBottom: 'none'
                              }}
                            >
                              {groupSearchQuery ? 'No matching groups found' : 'No groups available'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </AdminFormGroup>
            </AdminGroupsForm>
          </AdminModalBody>
          <AdminModalButtonContainer>
            <Button variant="contained" onClick={() => handleSubmitApiKey()}>
              Create API Key
            </Button>
          </AdminModalButtonContainer>
        </AdminModalInnerContainer>
      </AdminModal>

      {/* (2) Completed Modal */}
      <AdminModal
        open={modal2Open}
        onClose={() => setModal2Open(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CreateApiKeyModalContainer elevation={2}>
          <AdminTitle>
            <div className="d-flex">
              <p className="ps-1">Api Key</p>
            </div>
          </AdminTitle>
          <ApiKeyModalBody>
            <p className="pt-2 pb-2">Please Copy the API Key below</p>
            <div className="d-flex flex-row pt-2 pb-2">
              <input
                disabled={true}
                placeholder={apiKey}
                id="apiKey"
                name="apiKey"
                className="custom-input"
              />
              <Button onClick={copyText}>Copy</Button>
            </div>
          </ApiKeyModalBody>
          <Paper className="p-3">
            <Button variant="contained" onClick={() => setModal2Open(false)}>
              Confirm Key Copy
            </Button>
          </Paper>
        </CreateApiKeyModalContainer>
      </AdminModal>
    </>
  );
}